import { useEffect, useMemo, useState } from "react";
import { faqItems } from "@/mocks/faq";

const PAGE_SIZE = 8;

export default function Faq() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("전체");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);

  const categories = useMemo(() => {
    const list: string[] = [];
    faqItems.forEach((item) => {
      if (!list.includes(item.category)) list.push(item.category);
    });
    return ["전체", ...list];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqItems.filter((item) => {
      const matchCategory = category === "전체" || item.category === category;
      const matchQuery =
        q === "" ||
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q);
      return matchCategory && matchQuery;
    });
  }, [query, category]);

  useEffect(() => {
    setPage(1);
  }, [query, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleToggle = (id: number) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const goToPage = (target: number) => {
    const next = Math.min(Math.max(target, 1), totalPages);
    setPage(next);
    setOpenId(null);
  };

  return (
    <section id="faq" className="py-16 md:py-24 bg-background-100">
      <div className="mx-auto max-w-4xl px-4 md:px-6">
        <div className="text-center">
          <p className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-xs font-medium text-primary-700 tracking-wide">
            <i className="ri-question-answer-line"></i>
            FAQ
          </p>
          <h2 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
            자주 묻는 <span className="text-accent-600">질문</span>
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
            형사·성범죄·경제범죄·교통·가사·회생까지,
            <br className="hidden md:block" /> 의뢰인들이 가장 많이 묻는 질문을
            모았습니다.
          </p>
        </div>

        <div className="mt-6 md:mt-8">
          <div className="relative">
            <span className="absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="궁금한 내용을 검색해보세요 (예: 음주운전, 재산분할)"
              className="w-full rounded-lg border border-background-300 bg-white py-3 pl-12 pr-12 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 md:py-4"
            />
            {query !== "" && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="검색어 지우기"
                className="absolute right-4 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-background-200"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5 md:mt-4 md:gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors md:px-4 md:py-2 md:text-sm ${
                  category === c
                    ? "bg-primary-500 text-white"
                    : "border border-background-300 bg-white text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <p className="mt-3 text-xs text-foreground-500 md:mt-4 md:text-sm">
            총{" "}
            <span className="font-semibold text-primary-600">
              {filtered.length}
            </span>
            개의 질문
          </p>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-4 space-y-3">
            {pageItems.map((item) => {
              const isOpen = openId === item.id;
              return (
                <div
                  key={item.id}
                  className={`overflow-hidden rounded-lg border bg-white transition-colors ${
                    isOpen ? "border-primary-300" : "border-background-200"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleToggle(item.id)}
                    className="flex w-full items-center gap-3 px-4 py-4 text-left md:px-5"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-50 text-xs font-bold text-primary-600">
                      Q
                    </span>
                    <span className="flex-1">
                      <span className="block text-[11px] font-medium text-accent-600">
                        {item.category}
                      </span>
                      <span className="mt-0.5 block text-sm font-semibold leading-snug text-foreground-950 md:text-base">
                        {item.question}
                      </span>
                    </span>
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center text-foreground-400 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-lg"></i>
                    </span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-background-200 px-4 py-4 md:px-5">
                      <p className="text-sm leading-relaxed text-foreground-700">
                        {item.answer}
                      </p>
                      <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-4 py-3">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-accent-600">
                          <i className="ri-checkbox-circle-line text-base"></i>
                        </span>
                        <p className="text-xs leading-relaxed text-accent-900 md:text-sm">
                          <span className="font-semibold">확인 포인트</span> ·{" "}
                          {item.points}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-background-200 bg-white py-16 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm font-semibold text-foreground-900">
              검색 결과가 없습니다
            </p>
            <p className="mt-1 text-xs text-foreground-500">
              다른 키워드로 검색하거나 카테고리를 변경해보세요.
            </p>
          </div>
        )}

        {totalPages > 1 && (
          <>
            {/* 모바일: 컴팩트 인디케이터 */}
            <nav
              aria-label="FAQ 페이지"
              className="mt-8 flex sm:hidden items-center justify-center gap-3"
            >
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="이전 페이지"
                className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 bg-white text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-background-300 disabled:hover:text-foreground-600"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              <span className="min-w-[64px] text-center text-sm font-semibold text-foreground-950">
                {currentPage}
                <span className="font-normal text-foreground-500"> / {totalPages}</span>
              </span>
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="다음 페이지"
                className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 bg-white text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-background-300 disabled:hover:text-foreground-600"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </nav>

            {/* 데스크톱: 번호 목록 */}
            <nav
              aria-label="FAQ 페이지"
              className="mt-8 hidden sm:flex items-center justify-center gap-1.5"
            >
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-white text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-background-300 disabled:hover:text-foreground-600"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              {pageNumbers.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => goToPage(p)}
                  className={`h-9 min-w-9 rounded-md px-2 text-sm font-medium transition-colors ${
                    p === currentPage
                      ? "bg-primary-500 text-white"
                      : "border border-background-300 bg-white text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-white text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-background-300 disabled:hover:text-foreground-600"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </nav>
          </>
        )}
      </div>
    </section>
  );
}